// ─────────────────────────────────────────────────────────────────────────────
// Display schema — hand-written mirror of vanir_display_schema.yaml (v0.1).
//
// Scope of THIS file: headline + compare fields only (~22). Evidence tier and
// hidden fields are intentionally out of scope; add them when AnalysisPanel is
// restructured into the group cards.
//
// Two rules that the rest of the app depends on:
//   1. warn / block are REAL PREDICATES (functions), not YAML strings. They
//      receive the whole project and can reach into _parcel_props,
//      readiness_themes, cross-field state.
//   2. Formatters return text/num/meta only (+ 'na' when the field is
//      fundamentally unavailable). renderCell(field, project) is the single
//      place that computes final cls (block > warn > ''), so a cell in the
//      compare table, the SelectedSiteCard and the AnalysisPanel are
//      guaranteed to render identical colour + text for the same value.
//
// A parity test at frontend/src/config/__tests__/displaySchema.parity.test.js
// asserts every {key, label, group, tier, better} here matches the YAML at
// frontend/src/config/vanir_display_schema.yaml. Keep them in sync.
//
// ── Deviations from YAML v0.1 (applied 2026-08-30) ──────────────────────────
// Every threshold below differs from the review-sheet spec. YAML committed
// alongside this file already reflects these; the parity test asserts match.
//
//  · Acid sulfate:  warn at Moderate+, never block (spec had block at High).
//  · EIA required:  warn, not block (spec had block).
//  · Dual feed:     no warn on "single feed"; text "Not available", plain.
//  · Parcel area:   warn threshold = 3 ha (spec: project.site_size_ha — not
//                   in payload; land={}, financials={}; noted as assumption).
//  · Heritage:      backend buffer_m default is 500 m, not 5 km. Label uses
//                   "within 500 m" and warn fires at nearest < 200 m
//                   (matches EIA-trigger service copy).
//  · Removed rows:  design_temp_min_c (evidence tier); nearest_grid_operator
//                   (renders as meta on substation); inside_protected_area
//                   (protected-area cell text becomes "Inside · {type}"
//                   and blocks via its own predicate).
// ─────────────────────────────────────────────────────────────────────────────

import {
  NOT_ASSESSED, NO_DATA,
  formatDistanceKm, formatDistanceM, nearestHomeText, distanceKey,
  formatAreaHa, areaKey,
  formatVoltageKv, formatPercent, formatElevationM,
  humanizeSubstationName,
  projectValue,
  monthsSince,
} from '../utils/format';
import { planCodeName, planCodeSuitability } from '../utils/planCodes';

// Zoning read plan by plan (backend services/norway/plan_status, 2026-10-17).
// Order in the NA view: current (clean) > future_A > current with a plan
// conflict > future_B > unknown > not_allowed.
export const ZONING_STATUS_LABEL = {
  current: 'Zoned for a plant (regulatory plan)',
  future_A: 'Earmarked in the master plan (A)',
  future_B: 'Plan proposal allows a plant (B)',
  not_allowed: 'Zoning does not allow a plant',
  unknown: 'Zoning unknown',
};
export function zoningStatusRank(status, conflict) {
  if (status === 'current') return conflict ? 2 : 0;
  return { future_A: 1, future_B: 3, unknown: 4, not_allowed: 5 }[status] ?? 6;
}
const LAND_USE_STATUS = { 1: 'current land use', 2: 'future land use (framtidig)', 3: 'further development' };
import { zoneForKommune } from '../utils/projectCalcs';

const VOLTAGE_SOURCE = {
  nve: 'NVE',
  nve_transmission_level: 'Statnett station; NVE gives the transmission level, not the voltage',
  estimated: 'estimated from the owner; NVE records none',
};

// SafeSeaNet vessel classes as the Port & offtake panels name them.
export const VESSEL_CLASS_LABEL = {
  gas_carrier:         'Gas carriers',
  tanker:              'Tankers',
  bulk:                'Dry bulk',
  general_cargo_roro:  'General cargo / ro-ro',
  container:           'Container',
  passenger_cruise:    'Passenger / cruise',
  offshore:            'Offshore',
  fishing_aquaculture: 'Fishing / aquaculture',
  other:               'Other',
  unmapped:            'Unmapped',
};

export const SCHEMA_VERSION = '0.1.0';

// ─── Groups (order matches YAML `groups:` block) ────────────────────────────
export const GROUPS = [
  { id: 'power',        label: 'Power',                 themes: ['power'] },
  { id: 'connectivity', label: 'Connectivity',          themes: ['fiber'] },
  { id: 'site',         label: 'Site & access',         themes: ['site_formation', 'access', 'expansion'] },
  { id: 'water',        label: 'Water & cooling',       themes: ['water'] },
  { id: 'environment',  label: 'Environment & hazards', themes: ['environment'] },
  { id: 'neighbours',   label: 'Neighbours',            themes: [] },
  { id: 'permitting',   label: 'Permitting & planning', themes: ['planning', 'permitting'] },
  { id: 'economics',    label: 'Economics',             themes: [] },
];

// Ammonia mode (2026-10-04): an org whose hydrogen use-case is labelled
// "Ammonia" (branding.use_case_label.hydrogen) renders the Projects card
// and compare table with ammonia-plant groups. Theme keys match the
// backend ammonia_v1 index (app/services/ammonia_readiness.py).
// Connectivity is gone (fibre is irrelevant to an electrolyser), a
// "Port & offtake" group appears, and the economics row loses CO₂
// (that's e-methanol logic — ammonia takes nitrogen from air).
export const GROUPS_AMMONIA = [
  // The siting brief's own tests, as the search applied them (2026-10-12):
  // zoning, capacity test and grid point, qualifying harbour, nearest home,
  // free and flat land, heritage. Empty for sites saved outside the screen.
  { id: 'brief',        label: 'Siting brief',          themes: [] },
  { id: 'power',        label: 'Power',                 themes: ['grid', 'power_price'] },
  { id: 'port',         label: 'Port & offtake',        themes: ['port'] },
  { id: 'water',        label: 'Water',                 themes: ['water'] },
  { id: 'site',         label: 'Site & access',         themes: ['land'] },
  { id: 'environment',  label: 'Environment & safety',  themes: ['setback'] },
  { id: 'neighbours',   label: 'Neighbours',            themes: [] },
  { id: 'permitting',   label: 'Permitting & planning', themes: ['permitting'] },
  { id: 'economics',    label: 'Economics',             themes: [] },
];
export function groupsFor(mode) {
  return mode === 'ammonia' ? GROUPS_AMMONIA : GROUPS;
}
// A field declared for 'hydrogen' also shows in 'ammonia' unless it opts
// out with excludeFor: ['ammonia'].
function modeMatches(f, mode) {
  if (f.mode === 'both') return !(f.excludeFor || []).includes(mode);
  if (f.mode === mode) return true;
  if (mode === 'ammonia' && f.mode === 'hydrogen') return !(f.excludeFor || []).includes('ammonia');
  return false;
}

// The nine themes compute_powered_land_readiness produces. Constant here so
// confidence() can count against a fixed denominator — a theme missing from
// the payload counts as NOT assessed. Keep in sync with the backend.
export const THEME_KEYS = [
  'power', 'planning', 'water', 'site_formation', 'access',
  'fiber', 'environment', 'permitting', 'expansion',
];
// ammonia_v1 themes — keep in sync with backend WEIGHTS.
export const AMMONIA_THEME_KEYS = [
  'grid', 'port', 'water', 'power_price', 'land', 'setback', 'permitting',
];
// Which index scored this project? The backend stamps readiness_index on
// ammonia-scored enrichments; anything else is the 9-theme DC index.
export function themeKeysFor(project) {
  const idx = projectValue(project, 'readiness_index');
  return typeof idx === 'string' && idx.startsWith('ammonia') ? AMMONIA_THEME_KEYS : THEME_KEYS;
}

// ─── Internal helpers ───────────────────────────────────────────────────────
const trueish = (v) => v === true || v === 'true' || v === 1;
const isBlank = (v) => v == null || v === '';
// First non-blank value across alternative enrichment keys. Sites saved
// from /sites/search and sites saved from a map click name the same
// facts differently (nearest_port_name vs port_name, total_area_ha vs
// _parcel_props.areal_ha); the card must read both shapes.
const firstValue = (project, paths) => {
  for (const path of paths) {
    const v = projectValue(project, path);
    if (!isBlank(v)) return v;
  }
  return undefined;
};
const parcelAreaHa = (p) => firstValue(p, ['_parcel_props.areal_ha', 'total_area_ha', 'areal_ha', 'total_parcel_area_ha']);
const boolFalseDefault = (project, path, fallback = false) => {
  const v = projectValue(project, path);
  return v == null ? fallback : trueish(v);
};

// Cell shape. Formatters return text/num/meta (+ 'na' when the value is
// fundamentally unavailable). renderCell later applies block/warn cls.
function cell({ text, num = null, meta = null, cls = '' }) {
  return { text, num, meta, cls };
}

// ─── Neighbour categories ──────────────────────────────────────────────────
//
// CSV audit (v0.1 review sheet):
//   OSM proximity families (16):     agricultural, correctional,
//     emergency_services, fuel_storage, gas_pipeline, government, hospital,
//     industrial, military, power_plant, railway, religious, school,
//     university, wastewater, water_tower
//   EEA families (5):                  agriculture, chemical, industrial,
//     power_plant, refinery
//   Merged (OSM key ↔ EEA key both exist, 3): agricultural↔agriculture,
//     industrial↔industrial, power_plant↔power_plant.
//   Union = 16 + 5 − 3 = 18 distinct categories. All 18 are covered here.
export const NEIGHBOUR_CATS = [
  // ── OSM ↔ EEA merged (3) ─────────────────
  { key: 'agricultural',        label: 'intensive agriculture', eea: 'agriculture'  },
  { key: 'industrial',          label: 'industrial facility',   eea: 'industrial'   },
  { key: 'power_plant',         label: 'power generation',      eea: 'power_plant'  },
  // ── EEA only (2) ─────────────────────────
  { key: 'chemical',            label: 'chemical/pharma',       eea: 'chemical'     },
  { key: 'refinery',            label: 'refinery',              eea: 'refinery'     },
  // ── OSM only (13) ────────────────────────
  { key: 'fuel_storage',        label: 'fuel storage',          eea: null },
  { key: 'wastewater',          label: 'wastewater plant',      eea: null },
  { key: 'military',            label: 'military site',         eea: null },
  { key: 'hospital',            label: 'hospital/medical',      eea: null },
  { key: 'school',              label: 'school/daycare',        eea: null },
  { key: 'university',          label: 'university',            eea: null },
  { key: 'religious',           label: 'place of worship',      eea: null },
  { key: 'emergency_services',  label: 'police/fire/EMS',       eea: null },
  { key: 'government',          label: 'government',            eea: null },
  { key: 'railway',             label: 'railway',               eea: null },
  { key: 'correctional',        label: 'prison',                eea: null },
  { key: 'gas_pipeline',        label: 'gas pipeline',          eea: null },
  { key: 'water_tower',         label: 'water tower',           eea: null },
];
function neighboursFlagged(p) {
  const out = [];
  for (const c of NEIGHBOUR_CATS) {
    const osmW = trueish(projectValue(p, `proximity_${c.key}_warn`));
    const osmD = projectValue(p, `proximity_${c.key}_distance_m`);
    let eeaW = false, eeaD = null;
    if (c.eea) {
      eeaW = trueish(projectValue(p, `eea_${c.eea}_warn`));
      eeaD = projectValue(p, `eea_${c.eea}_distance_m`);
    }
    if (!osmW && !eeaW) continue;
    const distances = [osmD, eeaD].filter(x => x != null && Number.isFinite(x));
    const d = distances.length ? Math.min(...distances) : null;
    out.push({ label: c.label, d });
  }
  return out.sort((a, b) => (a.d ?? Infinity) - (b.d ?? Infinity));
}

// ─── Field definitions ─────────────────────────────────────────────────────
// Each formatter returns text/num/meta (+ 'na' when unavailable). Never sets
// warn or block — those come from the field's own predicates via renderCell.
const SCREENED = (p) => !!projectValue(p, 'harbour_screen');
// WattApp carries two dates per station: updated_at, when the grid company
// last published its figures (the age of the data), and data_date, the date
// the figures refer to. Only updated_at says how old the data is; sites
// saved before 2026-10-12 have data_date alone until they are refreshed.
export const STALE_MONTHS = 12;
export function capacityAge(gp) {
  if (!gp) return { text: 'WattApp', stale: false };
  if (gp.updated_at) {
    const m = monthsSince(gp.updated_at);
    const stale = m != null && m >= STALE_MONTHS;
    return { text: `WattApp, updated ${gp.updated_at}${stale ? ` (${m} months ago)` : ''}`, stale };
  }
  return { text: gp.data_date ? `WattApp, figures as of ${gp.data_date}` : 'WattApp', stale: false };
}
const screenPoint = (p) => projectValue(p, 'harbour_screen.grid_point');
const HARBOUR_KIND = { QUAY: 'quay', HARBOUR: 'harbour', PORT_FACILITY: 'port facility', REGISTER: 'port' };
const zoneFit = (code) => {
  const v = planCodeSuitability(code);
  if (v == null) return null;
  return v >= 0.75 ? 'fits a plant' : v >= 0.4 ? 'check the plan provisions' : 'needs rezoning';
};

// A site's price zone is its municipality's (projectCalcs.zoneForKommune).
// Sites saved before 2026-10-13 can carry a zone from the old position rule
// (Karmøy as NO4 or NO5, Meløy as NO3) and a price for that zone; such a
// figure is not shown until the site is refreshed.
function zoneCheck(p) {
  const stored = projectValue(p, 'price_zone') || projectValue(p, 'nearest_grid_price_zone') || null;
  const knr = projectValue(p, 'kommunenummer') || projectValue(p, '_parcel_props.kommunenummer')
    || projectValue(p, '_parcel_props.kommunenr');
  const zone = zoneForKommune(knr);
  if (zone && stored && zone !== stored) {
    return { zone, stale: cell({ text: 'Refresh needed', meta: `stored figure is for ${stored}; the site is in ${zone}`, cls: 'na' }) };
  }
  return { zone: zone || stored, stale: null };
}

export const FIELDS = [
  // ── Siting brief (ammonia; group 'brief' only exists in GROUPS_AMMONIA) ──
  {
    key: 'zone_name',
    group: 'brief',
    label: 'Zoning',
    tier: 'headline',
    showWhen: 'always',
    mode: 'hydrogen',
    warn: (p) => { const c = projectValue(p, 'zone_code'); const v = c != null ? planCodeSuitability(c) : null; return v != null && v < 0.75; },
    formatter: (p) => {
      const code = projectValue(p, 'zone_code');
      const name = projectValue(p, 'zone_name') || (code != null ? planCodeName(code) : null);
      if (isBlank(name) && isBlank(code)) return cell({ text: NOT_ASSESSED, cls: 'na' });
      const layer = projectValue(p, 'zone_layer');
      const ab = projectValue(p, 'zone_arealbruksstatus');
      const meta = [code != null ? `code ${code}` : null, code != null ? zoneFit(code) : null,
                    layer && projectValue(p, 'zoning_status') ? layer : null,
                    ab != null && LAND_USE_STATUS[ab] ? LAND_USE_STATUS[ab] : null].filter(Boolean).join(' · ');
      return cell({ text: name || `Code ${code}`, meta: meta || null });
    },
  },
  {
    key: 'zoning_status',
    group: 'brief',
    label: 'Zoning status',
    tier: 'headline',
    showWhen: (p) => projectValue(p, 'zoning_status') != null,
    mode: 'hydrogen',
    better: 'higher',
    warn: (p) => !!projectValue(p, 'plan_conflict') || projectValue(p, 'zoning_status') === 'future_B',
    block: (p) => projectValue(p, 'zoning_status') === 'not_allowed',
    formatter: (p) => {
      const st = projectValue(p, 'zoning_status');
      if (!st) return cell({ text: NOT_ASSESSED, cls: 'na' });
      const conflict = !!projectValue(p, 'plan_conflict');
      const reason = projectValue(p, 'zoning_status_reason');
      const action = projectValue(p, 'plan_conflict_text');
      const pr = projectValue(p, 'plan_readings') || {};
      const plans = [];
      if (pr.reguleringsplan?.plan_name) plans.push(`Regulatory plan: ${pr.reguleringsplan.plan_name}${pr.reguleringsplan.in_force ? ` (in force ${pr.reguleringsplan.in_force})` : ''}`);
      if (pr.kommuneplan?.plan_name) plans.push(`Master plan: ${pr.kommuneplan.plan_name}${pr.kommuneplan.in_force ? ` (in force ${pr.kommuneplan.in_force})` : ''}`);
      for (const k of ['rp_forslag', 'kp_forslag']) {
        const f = pr[k];
        if (f?.code && f.plan_name) plans.push(`Proposal: ${f.plan_name}${f.updated ? ` (updated ${f.updated})` : ''}`);
      }
      const meta = [reason, conflict ? action : null, plans.join(' · ') || null].filter(Boolean).join(' — ');
      const text = (ZONING_STATUS_LABEL[st] || st) + (conflict ? ' · plan conflict' : '');
      return cell({ text, meta: meta || null, cls: st === 'unknown' ? 'na' : '', num: 5 - zoningStatusRank(st, conflict) });
    },
  },
  {
    key: 'planning_started',
    group: 'brief',
    label: 'Planning started',
    tier: 'headline',
    showWhen: (p) => Array.isArray(projectValue(p, 'planning_started')) && projectValue(p, 'planning_started').length > 0,
    mode: 'hydrogen',
    warn: () => true,
    formatter: (p) => {
      const list = projectValue(p, 'planning_started') || [];
      if (!list.length) return cell({ text: NO_DATA, cls: 'na' });
      const first = list[0];
      const text = `${first.plan_name || first.plan_id}${first.notice_date ? ` (notice ${first.notice_date})` : ''}`;
      const rest = list.slice(1).map(x => `${x.plan_name || x.plan_id}${x.notice_date ? ` (${x.notice_date})` : ''}`);
      const meta = [first.area_ha != null ? `${Math.round(first.area_ha)} ha planned` : null,
                    'boundary only, no land use yet · DIBK Planlegging igangsatt',
                    rest.length ? `also: ${rest.join('; ')}` : null].filter(Boolean).join(' · ');
      return cell({ text, meta });
    },
  },
  {
    key: 'data_dates',
    group: 'brief',
    label: 'Data as of',
    tier: 'headline',
    showWhen: (p) => ['parcel_snapshot_date', 'plan_readings.fetched_at', 'refreshed_at', 'enriched_at'].some(k => projectValue(p, k)),
    mode: 'hydrogen',
    formatter: (p) => {
      const day = (v) => (v ? String(v).slice(0, 10) : null);
      const parts = [
        day(projectValue(p, 'parcel_snapshot_date')) ? `parcels ${day(projectValue(p, 'parcel_snapshot_date'))}` : null,
        day(projectValue(p, 'plan_readings.fetched_at')) ? `plans read ${day(projectValue(p, 'plan_readings.fetched_at'))}` : null,
        day(projectValue(p, 'harbour_screen.grid_point.updated_at')) ? `capacity published ${day(projectValue(p, 'harbour_screen.grid_point.updated_at'))}` : null,
        day(projectValue(p, 'statnett_above.snapshot_date')) ? `Statnett ${day(projectValue(p, 'statnett_above.snapshot_date'))}` : null,
      ].filter(Boolean);
      const last = day(projectValue(p, 'refreshed_at')) || day(projectValue(p, 'enriched_at'));
      return cell({ text: last ? `Refreshed ${last}` : 'Dates on file', meta: parts.join(' · ') || null });
    },
  },
  {
    key: 'screen_tests_passed',
    group: 'brief',
    label: 'Capacity test',
    tier: 'headline',
    showWhen: SCREENED,
    mode: 'hydrogen',
    better: 'higher',
    // Stale figures, or a station that no longer passes after a refresh.
    warn: (p) => {
      const t = projectValue(p, 'screen_tests_passed');
      return capacityAge(screenPoint(p)).stale || (Array.isArray(t) && t.length === 0) || projectValue(p, 'capacity_covers_plant') === false;
    },
    // A Statnett restriction above the station (services/tso_constraints):
    // the published free MW cannot be had on ordinary terms.
    block: (p) => !!projectValue(p, 'tso_constraint'),
    formatter: (p) => {
      const gp = projectValue(p, 'harbour_screen.grid_point');
      if (!gp) return cell({ text: NOT_ASSESSED, cls: 'na' });
      const tso = projectValue(p, 'tso_constraint');
      if (tso) return cell({ text: 'Fails: Statnett restriction', meta: tso.short });
      const tests = projectValue(p, 'screen_tests_passed');
      const text = !Array.isArray(tests) ? 'Passes the screen'
        : tests.length ? `Passes ${tests.join(', ')}` : 'No test passed on the latest figures';
      const covers = projectValue(p, 'capacity_covers_plant');
      const plantMw = projectValue(p, 'plant_mw');
      const fit = covers === true ? `covers the ${plantMw ? Math.round(plantMw) + ' MW ' : ''}plant on its own`
        : covers === false ? `short for the ${plantMw ? Math.round(plantMw) + ' MW ' : ''}plant (${Math.round(gp.available_mw)} MW free)` : null;
      const meta = [gp.name, gp.available_mw != null ? `${Math.round(gp.available_mw)} MW free` : null, fit,
                    gp.queue_mw != null ? `${Math.round(gp.queue_mw)} MW queued at ${gp.owner || 'the grid company'}` : null,
                    gp.reserved_mw ? `${Math.round(gp.reserved_mw)} MW reserved` : null,
                    gp.distance_km != null ? formatDistanceKm(gp.distance_km) : null,
                    capacityAge(gp).text].filter(Boolean).join(' · ');
      return cell({ text, meta, num: gp.available_mw != null ? Number(gp.available_mw) : null });
    },
  },
  {
    key: 'harbour_screen.harbour.name',
    group: 'brief',
    label: 'Qualifying harbour',
    tier: 'headline',
    showWhen: SCREENED,
    mode: 'hydrogen',
    better: 'lower',
    formatter: (p) => {
      const h = projectValue(p, 'harbour_screen.harbour');
      if (!h || isBlank(h.name)) return cell({ text: NOT_ASSESSED, cls: 'na' });
      const lim = projectValue(p, 'harbour_screen.limits.max_harbour_km');
      const meta = [h.distance_km != null ? formatDistanceKm(h.distance_km) : null,
                    h.kind ? (HARBOUR_KIND[h.kind] || String(h.kind).toLowerCase()) : null,
                    lim != null ? `brief ≤${lim} km` : null].filter(Boolean).join(' · ');
      return cell({ text: h.name, meta: meta || null, num: h.distance_km != null ? distanceKey(h.distance_km) : null });
    },
  },
  {
    key: 'harbour_screen.nearest_home_m',
    group: 'brief',
    label: 'Nearest home',
    tier: 'headline',
    showWhen: SCREENED,
    mode: 'hydrogen',
    better: 'higher',
    formatter: (p) => {
      const m = projectValue(p, 'harbour_screen.nearest_home_m');
      const lim = projectValue(p, 'harbour_screen.limits.min_neighbour_m');
      const h = nearestHomeText(m, lim);
      const meta = h.atLimit ? `the land stops at the brief's ${lim} m limit` : (lim != null ? `brief ≥${lim} m` : null);
      return cell({ text: h.text, meta, num: m == null ? 5000 : Number(m) });
    },
  },
  {
    key: 'free_land_ha',
    group: 'brief',
    label: 'Free of buildings',
    tier: 'headline',
    showWhen: (p) => projectValue(p, 'free_land_ha') != null,
    mode: 'hydrogen',
    better: 'higher',
    formatter: (p) => {
      const ha = projectValue(p, 'free_land_ha');
      if (ha == null) return cell({ text: NOT_ASSESSED, cls: 'na' });
      const n = projectValue(p, 'buildings_cut_from_site');
      const meta = n ? `${n} registered building${n === 1 ? '' : 's'} left out · Matrikkelen` : 'no registered buildings · Matrikkelen';
      return cell({ text: `${formatAreaHa(ha)} contiguous`, meta, num: areaKey(ha) });
    },
  },
  {
    key: 'buildable_contiguous_ha',
    group: 'brief',
    label: 'Flat land',
    tier: 'headline',
    showWhen: (p) => projectValue(p, 'buildable_contiguous_ha') != null || SCREENED(p),
    mode: 'hydrogen',
    better: 'higher',
    formatter: (p) => {
      const ha = projectValue(p, 'buildable_contiguous_ha');
      const lim = projectValue(p, 'buildable_slope_limit_pct');
      const centre = projectValue(p, 'slope_pct');
      if (ha == null) {
        // The slope grid skips small sites whose centre point is clearly
        // flat (below 0.4x the limit; 4% for the ammonia brief's 10%) and
        // polygons too thin for six samples. Say what was measured rather
        // than "Not assessed".
        if (centre != null && Number(centre) <= 4) {
          return cell({ text: 'Flat at the centre', meta: `centre point ${Number(centre).toFixed(1)}% · no slope grid` });
        }
        return cell({ text: NOT_ASSESSED, cls: 'na' });
      }
      const avg = projectValue(p, 'slope_avg_pct');
      const max = projectValue(p, 'slope_max_pct');
      // The grid measures slope between points tens of metres apart; the
      // centre-point slope below is local, so a steep spot can sit inside a
      // site that is mostly flat. avg/max make that visible.
      const meta = [lim != null ? `slope ≤${lim}%` : null,
                    avg != null ? `grid avg ${Number(avg).toFixed(0)}%` : null,
                    max != null ? `max ${Number(max).toFixed(0)}%` : null].filter(Boolean).join(' · ');
      return cell({ text: `${formatAreaHa(ha)} contiguous`, meta: meta || 'elevation grid', num: areaKey(ha) });
    },
  },
  {
    key: 'har_kulturminne',
    group: 'brief',
    label: 'Heritage on the property',
    tier: 'headline',
    showWhen: 'always',
    mode: 'hydrogen',
    warn: (p) => trueish(projectValue(p, 'har_kulturminne')),
    formatter: (p) => {
      const v = projectValue(p, 'har_kulturminne');
      if (v == null) return cell({ text: NOT_ASSESSED, cls: 'na' });
      if (!trueish(v)) return cell({ text: 'None registered', meta: 'Matrikkelen' });
      const kw = projectValue(p, 'kulturminne_warning');
      const names = kw && Array.isArray(kw.names) ? kw.names.filter(Boolean) : [];
      return cell({ text: 'Registered', meta: names.length ? names.join(', ') : 'Matrikkelen flag · check with the county' });
    },
  },
  {
    key: 'constituent_count',
    group: 'brief',
    label: 'Parcels',
    tier: 'headline',
    showWhen: (p) => projectValue(p, 'constituent_count') != null,
    mode: 'hydrogen',
    better: 'lower',
    formatter: (p) => {
      const n = projectValue(p, 'constituent_count');
      if (n == null) return cell({ text: NOT_ASSESSED, cls: 'na' });
      const first = projectValue(p, 'matrikkelnummer');
      const more = projectValue(p, 'site_merged_parcels');
      const list = [first, ...(Array.isArray(more) ? more : [])].filter(Boolean);
      const uniq = [...new Set(list)];
      const meta = uniq.length ? uniq.slice(0, 4).join(', ') + (uniq.length > 4 ? '…' : '') : null;
      return cell({ text: `${n}`, meta, num: Number(n) });
    },
  },
  // ── Power ────────────────────────────────────────────────────────────────
  {
    key: 'nearest_grid_name',
    group: 'power',
    label: 'Connection substation',
    tier: 'headline',
    showWhen: 'always',
    mode: 'both',
    formatter: (p) => {
      const name = projectValue(p, 'nearest_grid_name');
      if (isBlank(name)) return cell({ text: NOT_ASSESSED, cls: 'na' });
      const operator = projectValue(p, 'nearest_grid_operator');
      // The station's owner, which can be the regional grid company above
      // the local one (Glomfjord is ARVA's; Meløy Nett serves Meløy).
      return cell({ text: humanizeSubstationName(name), meta: operator ? `station owner ${operator}` : null });
    },
  },
  {
    key: 'grid_area_company.name',
    group: 'power',
    label: 'Local grid company',
    tier: 'headline',
    // NVE area concession; Norway only (enrich-single and screened searches)
    showWhen: (p) => projectValue(p, 'grid_area_company.name') != null,
    mode: 'both',
    // The ⓘ says what the company is; a missing name reads "Not assessed",
    // never the explanation alone (2026-10-13 review).
    formatter: (p) => {
      const name = projectValue(p, 'grid_area_company.name');
      if (isBlank(name)) return cell({ text: NOT_ASSESSED, cls: 'na' });
      return cell({ text: name });
    },
  },
  {
    key: 'nearest_grid_voltage_kv',
    group: 'power',
    label: 'Voltage',
    tier: 'headline',
    showWhen: 'always',
    mode: 'both',
    better: 'higher',
    formatter: (p) => {
      const v = projectValue(p, 'nearest_grid_voltage_kv');
      if (v == null && SCREENED(p)) return cell({ text: 'Not published', meta: 'the grid company publishes MW only', cls: 'na' });
      if (v == null) return cell({ text: NOT_ASSESSED, cls: 'na' });
      // Where the figure comes from (2026-10-13): NVE records a voltage for
      // most grid company stations, only the transmission level for
      // Statnett's, and none for some; those were guessed from the owner.
      const src = projectValue(p, 'nearest_grid_voltage_source');
      // No kV figure for a Statnett station: NVE records none (every one of
      // the 251 reads 420), and ours had no better source. Iledalsvågen read
      // 132 kV, NVE 420, and neither is a measurement (2026-10-13 review).
      if (src === 'nve_transmission_level' || /statnett/i.test(projectValue(p, 'nearest_grid_operator') || '')) {
        return cell({ text: 'Transmission level', meta: 'Statnett station; NVE records no voltage for it', num: Number(v) });
      }
      const meta = VOLTAGE_SOURCE[src] || ((p?.country || 'NO') === 'NO' ? 'not checked against NVE' : null);
      return cell({ text: formatVoltageKv(v), meta, num: Number(v) });
    },
  },
  {
    key: 'nearest_grid_distance_km',
    group: 'power',
    label: 'Grid distance',
    tier: 'headline',
    showWhen: 'always',
    mode: 'both',
    better: 'lower',
    warn: (p) => {
      const km = projectValue(p, 'nearest_grid_distance_km');
      return km != null && km > 20;
    },
    formatter: (p) => {
      const km = projectValue(p, 'nearest_grid_distance_km');
      if (km == null) return cell({ text: NOT_ASSESSED, cls: 'na' });
      return cell({ text: formatDistanceKm(km), num: distanceKey(km) });
    },
  },
  // Capacity at the connection substation — NVE/Statnett figures stored on
  // data_layers.grid_connections (capacity_mw, available_cons_mw, queue_mw),
  // attached to every search result from 2026-10-05. Null = not disclosed
  // for that station; shown as Not assessed, never as 0.
  {
    key: 'nearest_grid_capacity_mw',
    group: 'power',
    label: 'Nameplate capacity',
    tier: 'headline',
    showWhen: 'always',
    mode: 'both',
    better: 'higher',
    formatter: (p) => {
      const v = projectValue(p, 'nearest_grid_capacity_mw');
      if (v == null && SCREENED(p)) return cell({ text: 'Not published', meta: 'free, reserved and queued MW only', cls: 'na' });
      if (v == null) return cell({ text: NOT_ASSESSED, cls: 'na' });
      return cell({ text: `${Math.round(v)} MW`, meta: 'NVE Nettanlegg', num: Number(v) });
    },
  },
  {
    key: 'nearest_grid_available_mw',
    group: 'power',
    label: 'Available for consumption',
    tier: 'headline',
    showWhen: 'always',
    mode: 'both',
    better: 'higher',
    warn: (p) => {
      const v = projectValue(p, 'nearest_grid_available_mw');
      return (v != null && v < 20) || (SCREENED(p) && capacityAge(screenPoint(p)).stale);
    },
    formatter: (p) => {
      const v = projectValue(p, 'nearest_grid_available_mw');
      if (v == null) return cell({ text: NOT_ASSESSED, cls: 'na' });
      const q = projectValue(p, 'nearest_grid_queue_mw');
      const src = SCREENED(p) ? capacityAge(screenPoint(p)).text : null;
      // The grid company's own queue at this station; Statnett's queue above
      // it is the next row.
      const meta = [q != null ? `grid company queue ${Math.round(q)} MW` : null, src].filter(Boolean).join(' · ');
      return cell({ text: `${Math.round(v)} MW`, meta: meta || null, num: Number(v) });
    },
  },
  {
    // Statnett's queue at the transmission station above the connection
    // point (services/statnett_lists.statnett_above). The 2026-10-13 review
    // found Bamble reading "0 MW in queue" above while Statnett's lists had
    // 581 MW queued at Grenland, the station above Roligheten; a connection
    // of 5 MW or more joins both queues.
    key: 'statnett_above',
    group: 'power',
    label: 'Statnett queue above',
    tier: 'headline',
    showWhen: (p) => (p?.country || 'NO') === 'NO',
    mode: 'both',
    better: 'lower',
    formatter: (p) => {
      const s = projectValue(p, 'statnett_above');
      if (!s) return cell({ text: NOT_ASSESSED, cls: 'na' });
      const where = s.basis === 'named' ? `${s.name}, above ${s.via}`
        : s.via ? `${s.name}, nearest station (assumed) to ${s.via}${s.named_missing ? ` (WattApp names ${s.named_missing}, not in the lists)` : ''}`
          : `${s.name}, nearest station (assumed) to the site`;
      const meta = [where,
                    s.reserved_mw != null ? `${Math.round(s.reserved_mw)} MW reserved` : null,
                    s.snapshot_date ? `Statnett ${s.snapshot_date}` : null].filter(Boolean).join(' · ');
      return cell({ text: `${Math.round(s.queued_mw || 0)} MW queued`, meta, num: Number(s.queued_mw || 0) });
    },
  },
  {
    // The physically nearest substation regardless of voltage — context
    // for the connection substation above, never a substitute for it.
    key: 'nearest_any_grid_name',
    group: 'power',
    label: 'Nearest substation (any kV)',
    tier: 'headline',
    showWhen: 'always',
    mode: 'both',
    better: 'lower',
    formatter: (p) => {
      const name = projectValue(p, 'nearest_any_grid_name');
      if (isBlank(name)) return cell({ text: NOT_ASSESSED, cls: 'na' });
      const kv = projectValue(p, 'nearest_any_grid_voltage_kv');
      const km = projectValue(p, 'nearest_any_grid_distance_km');
      const op = projectValue(p, 'nearest_any_grid_operator');
      // A Statnett station reads "transmission level", not a kV figure (see Voltage).
      const statnett = projectValue(p, 'nearest_any_grid_voltage_source') === 'nve_transmission_level' || /statnett/i.test(op || '');
      const level = statnett ? 'transmission level' : (kv != null ? `${kv} kV` : null);
      const meta = [level, km != null ? formatDistanceKm(km) : null, op || null].filter(Boolean).join(' · ');
      return cell({ text: humanizeSubstationName(name), meta, num: km != null ? distanceKey(km) : null });
    },
  },
  {
    // A second 66 kV+ station within 30 km: a precondition for a second
    // feed, not proof of one. Read "Dual feed possible: Available" for every
    // site until 2026-10-13 (any row of any voltage counted); not part of
    // the ammonia brief, so not shown there.
    key: 'dual_feed_available',
    group: 'power',
    label: 'Second substation (66 kV+, 30 km)',
    tier: 'headline',
    showWhen: 'always',
    mode: 'both',
    excludeFor: ['ammonia'],
    better: 'higher',
    formatter: (p) => {
      const v = projectValue(p, 'dual_feed_available');
      if (v == null) return cell({ text: NOT_ASSESSED, cls: 'na' });
      if (trueish(v)) {
        const secondName = projectValue(p, 'second_grid_name');
        const secondKm = projectValue(p, 'second_grid_distance_km');
        const secondKv = projectValue(p, 'second_grid_voltage_kv');
        const meta = [secondKv ? `${secondKv} kV` : null, secondKm != null ? formatDistanceKm(secondKm) : null,
                      'whether it can feed the site is for the grid company'].filter(Boolean).join(' · ');
        return cell({ text: secondName ? humanizeSubstationName(secondName) : 'Yes', meta, num: 1 });
      }
      return cell({ text: 'None found', num: 0 });
    },
  },

  // ── Connectivity ─────────────────────────────────────────────────────────
  {
    key: 'nearest_ixp_name',
    group: 'connectivity',
    label: 'Internet exchange',
    tier: 'headline',
    showWhen: 'always',
    mode: 'datacenter',
    better: 'lower',
    warn: (p) => {
      const km = projectValue(p, 'nearest_ixp_distance_km');
      return km != null && km > 150;
    },
    formatter: (p) => {
      const km = projectValue(p, 'nearest_ixp_distance_km');
      const name = projectValue(p, 'nearest_ixp_name');
      if (km == null) return cell({ text: NOT_ASSESSED, cls: 'na' });
      return cell({
        text: name || 'Unnamed IXP',
        meta: formatDistanceKm(km),
        num: distanceKey(km),
      });
    },
  },
  {
    key: 'subsea_landing_name',
    group: 'connectivity',
    label: 'Subsea landing',
    tier: 'headline',
    showWhen: 'always',
    mode: 'datacenter',
    better: 'lower',
    formatter: (p) => {
      if (projectValue(p, 'subsea_data_available') === false) return cell({ text: NOT_ASSESSED, cls: 'na' });
      const name = projectValue(p, 'subsea_landing_name');
      if (isBlank(name)) return cell({ text: NO_DATA, cls: 'na' });
      const km = projectValue(p, 'subsea_distance_km');
      const count = projectValue(p, 'subsea_cable_count') || 0;
      return cell({
        text: name,
        meta: `${formatDistanceKm(km)} · ${count} cable${count === 1 ? '' : 's'}`,
        num: distanceKey(km),
      });
    },
  },
  {
    key: 'fiber_diversity',
    group: 'connectivity',
    label: 'Fibre diversity',
    tier: 'headline',
    showWhen: 'always',
    mode: 'datacenter',
    warn: (p) => {
      const v = projectValue(p, 'fiber_diversity');
      return typeof v === 'string' && v.toLowerCase() === 'single';
    },
    formatter: (p) => {
      const v = projectValue(p, 'fiber_diversity');
      if (typeof v !== 'string' || !v) return cell({ text: NOT_ASSESSED, cls: 'na' });
      return cell({ text: v.charAt(0).toUpperCase() + v.slice(1) });
    },
  },

  // ── Site & access ────────────────────────────────────────────────────────
  // Owner-class v1 — derived from the kiinteistötunnus reserved-series
  // regex (state / yhteismetsä / common_area / public_road / railway /
  // municipal) + 'unknown' fallback for rural tila. Only rendered for FI
  // sites; NO/IT projects don't carry these fields.
  {
    key: 'fi_owner_class',
    group: 'site',
    label: 'Owner class',
    tier: 'headline',
    showWhen: (p) => (p?.country || '').toUpperCase() === 'FI',
    mode: 'both',
    formatter: (p) => {
      const cls  = projectValue(p, 'fi_owner_class');
      const conf = projectValue(p, 'fi_owner_class_confidence');
      const disp = projectValue(p, 'fi_kiinteistotunnus_display');
      if (cls == null) {
        if (projectValue(p, 'fi_owner_class_data_available') === false) {
          return cell({ text: NO_DATA, cls: 'na' });
        }
        return cell({ text: NOT_ASSESSED, cls: 'na' });
      }
      const label = {
        state:        'State (Metsähallitus)',
        yhteismetsä: 'Jointly-owned forest',
        common_area:  'Common area',
        public_road:  'Public road',
        railway:      'Railway',
        municipal:    'Municipal',
        unknown:      'Private / corporate (unclassified)',
      }[cls] || cls;
      const bits = [];
      if (conf) bits.push(`${conf} conf.`);
      if (disp) bits.push(disp);
      return cell({ text: label, meta: bits.join(' · ') || null });
    },
  },
  {
    key: '_parcel_props.areal_ha',
    group: 'site',
    label: 'Parcel area',
    tier: 'headline',
    showWhen: 'always',
    mode: 'both',
    better: 'higher',
    // Deviation: threshold is 3 ha (fallback). Spec says project.site_size_ha,
    // but the payload's `land` and `financials` JSONBs are empty for the seed
    // rows and there's no calculated-site-size field. Assumption listed in PR.
    warn: (p) => {
      const ha = parcelAreaHa(p);
      return ha != null && ha < 3;
    },
    formatter: (p) => {
      const ha = parcelAreaHa(p);
      if (ha == null) return cell({ text: NOT_ASSESSED, cls: 'na' });
      return cell({ text: formatAreaHa(ha), num: areaKey(ha) });
    },
  },
  {
    key: 'slope_pct',
    group: 'site',
    label: 'Slope (centre point)',
    tier: 'headline',
    showWhen: 'always',
    mode: 'both',
    better: 'lower',
    warn: (p) => {
      const v = projectValue(p, 'slope_pct');
      return v != null && v > 20;
    },
    formatter: (p) => {
      const v = projectValue(p, 'slope_pct');
      if (v == null) return cell({ text: NOT_ASSESSED, cls: 'na' });
      // 1 decimal — (0.5).toFixed(0) reads as "1%" which is wrong.
      return cell({ text: formatPercent(v, { fractionDigits: 1 }), num: Math.round(v * 10) / 10 });
    },
  },
  {
    key: 'elevation_m',
    group: 'site',
    label: 'Elevation',
    tier: 'headline',
    showWhen: 'always',
    mode: 'both',
    formatter: (p) => {
      const v = projectValue(p, 'elevation_m');
      if (v == null) return cell({ text: NOT_ASSESSED, cls: 'na' });
      return cell({ text: formatElevationM(v), num: Math.round(v) });
    },
  },
  {
    key: 'nearest_road_distance_km',
    group: 'site',
    label: 'Road access',
    tier: 'headline',
    showWhen: 'always',
    mode: 'both',
    better: 'lower',
    warn: (p) => {
      const km = projectValue(p, 'nearest_road_distance_km');
      const label = projectValue(p, 'road_class_label') || '';
      return (km != null && km > 1) || /private/i.test(label);
    },
    formatter: (p) => {
      const km = projectValue(p, 'nearest_road_distance_km');
      if (km == null) return cell({ text: NOT_ASSESSED, cls: 'na' });
      const label = projectValue(p, 'road_class_label') || '';
      return cell({ text: formatDistanceKm(km), meta: label || null, num: distanceKey(km) });
    },
  },

  // ── Water & cooling ──────────────────────────────────────────────────────
  {
    key: 'water_distance_km',
    group: 'water',
    label: 'Water source',
    tier: 'headline',
    showWhen: 'always',
    mode: 'both',
    better: 'lower',
    // Screened (ammonia) searches keep a site whose water source is beyond
    // the 15 km filter and flag it instead (2026-10-17).
    warn: (p) => !!projectValue(p, 'water_distance_flag'),
    formatter: (p) => {
      if (projectValue(p, 'fi_water_data_available') === false) return cell({ text: NOT_ASSESSED, cls: 'na' });
      const km = projectValue(p, 'water_distance_km');
      if (km == null) return cell({ text: NO_DATA, cls: 'na' });
      const name = projectValue(p, 'river_name') || projectValue(p, 'lake_name') || 'Unnamed';
      const kind = projectValue(p, 'water_capacity_class');
      const metaParts = [];
      if (kind) metaParts.push(kind);
      metaParts.push(formatDistanceKm(km));
      if (projectValue(p, 'water_distance_flag')) metaParts.push('beyond the 15 km water filter, kept as a flag');
      return cell({ text: name, meta: metaParts.join(' · '), num: distanceKey(km) });
    },
  },
  {
    key: 'free_cooling_days_yr',
    group: 'water',
    label: 'Free-cooling days per year',
    tier: 'headline',
    showWhen: 'always',
    mode: 'datacenter',
    better: 'higher',
    formatter: (p) => {
      const v = projectValue(p, 'free_cooling_days_yr');
      if (v == null) return cell({ text: NOT_ASSESSED, cls: 'na' });
      return cell({ text: String(v), num: v });
    },
  },
  {
    key: 'nearest_dh_plant_name',
    group: 'water',
    label: 'Waste-heat offtake',
    tier: 'headline',
    showWhen: 'always',
    mode: 'datacenter',
    better: 'lower',
    warn: (p) => {
      const km = projectValue(p, 'nearest_dh_plant_distance_km');
      return km != null && km > 15;
    },
    formatter: (p) => {
      const km = projectValue(p, 'nearest_dh_plant_distance_km');
      if (km == null) return cell({ text: NOT_ASSESSED, cls: 'na' });
      const name = projectValue(p, 'nearest_dh_plant_name') || 'plant';
      const mw = projectValue(p, 'nearest_dh_plant_mw');
      const meta = [mw ? `${mw} MW` : null, formatDistanceKm(km)].filter(Boolean).join(' · ');
      return cell({ text: name, meta, num: distanceKey(km) });
    },
  },

  // ── Environment & hazards ────────────────────────────────────────────────
  {
    key: 'nearest_protected_distance_km',
    group: 'environment',
    label: 'Protected area',
    tier: 'headline',
    showWhen: 'always',
    mode: 'both',
    better: 'higher',
    warn: (p) => {
      const km = projectValue(p, 'nearest_protected_distance_km');
      return km != null && km < 1 && !boolFalseDefault(p, 'inside_protected_area');
    },
    block: (p) => boolFalseDefault(p, 'inside_protected_area'),
    formatter: (p) => {
      if (projectValue(p, 'fi_protected_data_available') === false) return cell({ text: NOT_ASSESSED, cls: 'na' });
      const km = projectValue(p, 'nearest_protected_distance_km');
      if (km == null) return cell({ text: NOT_ASSESSED, cls: 'na' });
      const type = projectValue(p, 'nearest_protected_type') || 'Protected area';
      // Deviation: inside_protected_area folded into this row's text.
      if (boolFalseDefault(p, 'inside_protected_area')) {
        return cell({ text: `Inside · ${type}`, num: 0 });
      }
      return cell({ text: type, meta: formatDistanceKm(km), num: distanceKey(km) });
    },
  },
  {
    key: 'fi_cultural_heritage_count',
    group: 'environment',
    label: 'Heritage sites',
    tier: 'headline',
    // Finnish heritage register only; Norwegian sites carry har_kulturminne
    // and the Riksantikvaren check instead ("Heritage on the property").
    showWhen: (p) => String(p?.country || projectValue(p, 'country') || '').toUpperCase() === 'FI',
    mode: 'both',
    better: 'lower',
    // Backend `check_cultural_heritage_fi` uses buffer_m=500. Warn threshold
    // matches EIA-trigger service copy: within 200 m.
    warn: (p) => {
      const near = projectValue(p, 'fi_cultural_heritage_nearest_m');
      return near != null && near < 200;
    },
    formatter: (p) => {
      if (projectValue(p, 'fi_cultural_heritage_data_available') === false) return cell({ text: NOT_ASSESSED, cls: 'na' });
      const n = projectValue(p, 'fi_cultural_heritage_count');
      if (n == null) return cell({ text: NOT_ASSESSED, cls: 'na' });
      if (n === 0) return cell({ text: 'None within 500 m', num: 0 });
      const near = projectValue(p, 'fi_cultural_heritage_nearest_m');
      return cell({
        text: `${n} within 500 m`,
        meta: near != null ? `nearest ${formatDistanceM(near)}` : null,
        num: n,
      });
    },
  },
  {
    key: 'flood_100yr',
    group: 'environment',
    label: 'Flood zone (100-yr)',
    tier: 'headline',
    showWhen: 'flagged',
    mode: 'both',
    better: 'false',
    warn: (p) => (
      boolFalseDefault(p, 'flood_100yr') ||
      boolFalseDefault(p, 'fi_flood_100yr') ||
      boolFalseDefault(p, 'flood_zone')
    ),
    formatter: (p) => {
      const flagged = (
        boolFalseDefault(p, 'flood_100yr') ||
        boolFalseDefault(p, 'fi_flood_100yr') ||
        boolFalseDefault(p, 'flood_zone')
      );
      return cell({ text: flagged ? 'Within flood zone' : 'Clear', num: flagged ? 1 : 0 });
    },
  },
  {
    key: 'landslide_zone',
    group: 'environment',
    label: 'Landslide zone',
    tier: 'headline',
    showWhen: 'flagged',
    mode: 'both',
    better: 'false',
    warn: (p) => boolFalseDefault(p, 'landslide_zone') || boolFalseDefault(p, 'landslide'),
    formatter: (p) => {
      const v = boolFalseDefault(p, 'landslide_zone') || boolFalseDefault(p, 'landslide');
      return cell({ text: v ? 'Within landslide zone' : 'Clear', num: v ? 1 : 0 });
    },
  },
  {
    key: 'fi_acid_sulfate_probability',
    group: 'environment',
    label: 'Acid sulfate soil',
    tier: 'headline',
    showWhen: 'flagged',
    mode: 'both',
    // Deviation: warn at Moderate+, never block.
    warn: (p) => {
      if (projectValue(p, 'fi_acid_sulfate_data_available') === false) return false;
      if (!boolFalseDefault(p, 'fi_acid_sulfate_in_area')) return false;
      const prob = projectValue(p, 'fi_acid_sulfate_probability');
      return /very high|high|moderate/i.test(prob || '');
    },
    formatter: (p) => {
      if (projectValue(p, 'fi_acid_sulfate_data_available') === false) return cell({ text: NOT_ASSESSED, cls: 'na' });
      if (!boolFalseDefault(p, 'fi_acid_sulfate_in_area')) return cell({ text: 'Clear', num: 0 });
      const prob = projectValue(p, 'fi_acid_sulfate_probability');
      if (isBlank(prob)) return cell({ text: NOT_ASSESSED, cls: 'na' });
      const scoreMap = { 'Very high': 4, 'High': 3, 'Moderate': 2, 'Low': 1, 'Very low': 0 };
      return cell({ text: prob, num: scoreMap[prob] ?? 1 });
    },
  },

  // ── Neighbours ───────────────────────────────────────────────────────────
  // Summary row that replaces the 22 individual proximity rows in today's
  // table. Sorted nearest-first; empty state is a positive line, not blank.
  {
    key: 'neighbours_summary',
    group: 'neighbours',
    label: 'Neighbours flagged',
    tier: 'headline',
    showWhen: 'always',
    mode: 'both',
    warn: (p) => neighboursFlagged(p).length > 0,
    formatter: (p) => {
      const flagged = neighboursFlagged(p);
      if (!flagged.length) return cell({ text: 'Nothing flagged within 2 km', num: 0 });
      const text = flagged.map(f => `${f.label} ${formatDistanceM(f.d)}`).join(' · ');
      return cell({ text, num: flagged.length });
    },
  },

  // ── Permitting & planning ────────────────────────────────────────────────
  {
    key: 'fi_eia_summary',
    group: 'permitting',
    label: 'EIA',
    tier: 'headline',
    showWhen: 'always',
    mode: 'both',
    // Deviation: EIA required is warn, not block (project can still proceed
    // with an EIA — it's a lead-time and cost hit, not a blocker).
    warn: (p) => boolFalseDefault(p, 'fi_eia_required') || boolFalseDefault(p, 'fi_eia_likely'),
    formatter: (p) => {
      if (boolFalseDefault(p, 'fi_eia_required')) {
        const triggers = projectValue(p, 'fi_eia_triggers');
        const arr = Array.isArray(triggers) ? triggers : [];
        return cell({ text: 'Required', meta: arr.length ? arr.join('; ') : null, num: 2 });
      }
      if (boolFalseDefault(p, 'fi_eia_likely')) {
        const summary = projectValue(p, 'fi_eia_summary');
        return cell({ text: 'Likely', meta: summary || null, num: 1 });
      }
      if (projectValue(p, 'fi_eia_summary')) return cell({ text: 'No triggers', num: 0 });
      return cell({ text: NOT_ASSESSED, cls: 'na' });
    },
  },
  {
    key: '_parcel_props.tvist',
    group: 'permitting',
    label: 'Ownership dispute',
    tier: 'headline',
    showWhen: 'flagged',
    mode: 'both',
    better: 'false',
    block: (p) => trueish(projectValue(p, '_parcel_props.tvist')),
    formatter: (p) => {
      const v = trueish(projectValue(p, '_parcel_props.tvist'));
      return cell({ text: v ? 'Registered dispute' : 'No dispute', num: v ? 1 : 0 });
    },
  },

  // ── Economics ────────────────────────────────────────────────────────────
  {
    key: 'nearest_co2_source',
    group: 'economics',
    label: 'CO₂ feedstock',
    tier: 'headline',
    showWhen: 'always',
    mode: 'hydrogen',   // shown for ammonia too (2026-10-05): e-fuel / urea side-streams
    better: 'lower',
    formatter: (p) => {
      const c = projectValue(p, 'nearest_co2_source');
      if (!c) return cell({ text: NOT_ASSESSED, cls: 'na' });
      const parts = [];
      // The register's figure, its year and what it measures (2026-10-13);
      // older saved sites carry only a bare kt figure.
      if (c.co2_kt != null) parts.push(c.year ? `${c.co2_kt} kt ${c.co2_measure || 'CO₂'} (${c.year})` : `${c.co2_kt} kt/yr`);
      if (c.biogenic_co2_kt) parts.push(`+${c.biogenic_co2_kt} kt biogenic`);
      if (c.capture?.state) parts.push(`capture ${c.capture.state}${c.capture.kt_per_year ? ` ~${c.capture.kt_per_year} kt/yr` : ''}`);
      if (c.distance_km != null) parts.push(formatDistanceKm(c.distance_km));
      return cell({
        text: c.name,
        meta: parts.join(' · '),
        num: c.distance_km != null ? distanceKey(c.distance_km) : null,
      });
    },
  },
  {
    key: 'port_name',
    group: 'economics',
    groupFor: { ammonia: 'port' },
    // Ammonia shows the register port with UN/LOCODE (Nearest port
    // (register)) and the brief's qualifying harbour; this third
    // "nearest port" from another register only confused the compare.
    excludeFor: ['ammonia'],
    label: 'Nearest port',
    tier: 'headline',
    showWhen: 'always',
    mode: 'hydrogen',
    better: 'lower',
    formatter: (p) => {
      // Search results name these nearest_port_*; the older map-click
      // save path used port_*. Accept both so neither shows Not assessed.
      const name = firstValue(p, ['port_name', 'nearest_port_name']);
      if (isBlank(name)) return cell({ text: NOT_ASSESSED, cls: 'na' });
      const km = firstValue(p, ['port_dist_km', 'nearest_port_distance_km']);
      const depth = firstValue(p, ['port_depth_m', 'nearest_port_depth_m']);
      const est = projectValue(p, 'nearest_port_depth_estimated');
      const meta = [
        km != null ? formatDistanceKm(km) : null,
        depth ? `${depth} m depth${est ? ' (est.)' : ''}` : null,
      ].filter(Boolean).join(' · ');
      return cell({ text: name, meta, num: km != null ? distanceKey(km) : null });
    },
  },

  // ── Ammonia / hydrogen additions (2026-10-04) ─────────────────────────
  // Power economics the search already computes but the card never showed.
  {
    key: 'spot_30d_avg_nok_mwh',
    group: 'power',
    label: 'Spot price (30-day avg)',
    tier: 'headline',
    showWhen: 'always',
    mode: 'hydrogen',
    better: 'lower',
    formatter: (p) => {
      const v = projectValue(p, 'spot_30d_avg_nok_mwh');
      if (v == null) return cell({ text: NOT_ASSESSED, cls: 'na' });
      const z = zoneCheck(p);
      if (z.stale) return z.stale;
      return cell({ text: `${Math.round(v)} NOK/MWh`, meta: z.zone ? `zone ${z.zone}` : null, num: Number(v) });
    },
  },
  {
    key: 'ppa_estimate_nok_mwh',
    group: 'power',
    label: 'PPA estimate',
    tier: 'headline',
    showWhen: 'always',
    mode: 'hydrogen',
    better: 'lower',
    formatter: (p) => {
      const v = projectValue(p, 'ppa_estimate_nok_mwh');
      if (v == null) return cell({ text: NOT_ASSESSED, cls: 'na' });
      const z = zoneCheck(p);
      if (z.stale) return z.stale;
      return cell({ text: `${Math.round(v)} NOK/MWh`, meta: z.zone ? `zone ${z.zone}` : null, num: Number(v) });
    },
  },
  {
    key: 'no_hydro_total_mw_within_radius',
    group: 'power',
    label: 'Hydropower nearby',
    tier: 'headline',
    showWhen: 'always',
    mode: 'hydrogen',
    better: 'higher',
    formatter: (p) => {
      const mw = projectValue(p, 'no_hydro_total_mw_within_radius');
      // A failed NVE lookup used to persist as "0 MW" — read as a fact.
      // Only show a number when the lookup actually succeeded.
      if (mw == null || projectValue(p, 'no_hydro_data_available') === false) return cell({ text: NOT_ASSESSED, cls: 'na' });
      const n = projectValue(p, 'no_hydro_count_within_radius');
      const r = projectValue(p, 'no_hydro_radius_km');
      const meta = [n != null ? `${n} plants` : null, r != null ? `within ${r} km` : null].filter(Boolean).join(' · ');
      return cell({ text: `${Math.round(mw)} MW`, meta, num: Number(mw) });
    },
  },
  // Residents within 2 km — SSB 250 m population grid (migration 035).
  // The Seveso exposure number for a toxic-gas plant. Absent until the
  // grid is loaded on the box; then every new search result carries it.
  {
    key: 'residents_within_2km',
    group: 'environment',
    label: 'Residents within 2 km',
    tier: 'headline',
    showWhen: 'always',
    mode: 'hydrogen',
    better: 'lower',
    warn: (p) => { const n = projectValue(p, 'residents_within_2km'); return n != null && n > 200; },
    block: (p) => { const n = projectValue(p, 'residents_within_2km'); return n != null && n > 5000; },
    formatter: (p) => {
      const n = projectValue(p, 'residents_within_2km');
      if (n == null) return cell({ text: NOT_ASSESSED, cls: 'na' });
      const n1 = projectValue(p, 'residents_within_1km');
      return cell({ text: `${Number(n).toLocaleString()} people`,
                    meta: n1 != null ? `${Number(n1).toLocaleString()} within 1 km · SSB 2024` : 'SSB 2024',
                    num: Number(n) });
    },
  },
  // Port & offtake rows read the /offtake/at payload that ProjectsV2
  // attaches as enrichment._offtake (see useOfftakeMany). The card has
  // the richer OfftakeBlock, so these are compare-table rows only.
  {
    key: '_offtake.nearest_port.name',
    group: 'port',
    label: 'Nearest port (register)',
    tier: 'headline',
    showWhen: 'always',
    mode: 'hydrogen',
    compareOnly: true,
    better: 'lower',
    formatter: (p) => {
      const port = projectValue(p, '_offtake.nearest_port');
      if (!port) return cell({ text: NOT_ASSESSED, cls: 'na' });
      const km = port.distance_km;
      return cell({ text: `${port.name}${port.unlocode ? ` (${port.unlocode})` : ''}`,
                    meta: km != null ? formatDistanceKm(km) : null,
                    num: km != null ? distanceKey(km) : null });
    },
  },
  {
    key: '_offtake.gas_tanker_calls',
    group: 'port',
    label: 'Gas / tanker calls (last full year)',
    tier: 'headline',
    showWhen: 'always',
    mode: 'hydrogen',
    compareOnly: true,
    better: 'higher',
    formatter: (p) => {
      const ma = projectValue(p, '_offtake.maritime_activity');
      const rows = Array.isArray(ma?.by_class) ? ma.by_class : [];
      if (!rows.length) return cell({ text: ma?.badge ? 'No port-call data' : NOT_ASSESSED, cls: 'na' });
      const years = Object.keys(rows[0].calls_per_year || {}).sort();
      const last = years[years.length - 1];
      let gas = 0, tank = 0;
      for (const r of rows) {
        const c = Number(r.calls_per_year?.[last] || 0);
        if (r.vessel_class === 'gas_carrier') gas += c;
        if (r.vessel_class === 'tanker') tank += c;
      }
      // Ship length of the class that made most calls in the latest full
      // year (2026-10-13). The largest class's p90 over all years gave Halsa
      // a 204 m "typical" ship from one cruise call.
      const t = ma.typical;
      const typical = t?.loa_p90_m != null
        ? `most calls ${VESSEL_CLASS_LABEL[t.vessel_class] || t.vessel_class}, LOA p90 ${Math.round(t.loa_p90_m)} m`
        : null;
      const meta = [last ? `in ${last}, whole port` : null, typical].filter(Boolean).join(' · ');
      return cell({ text: `${gas} gas · ${tank} tanker`, meta, num: gas + tank });
    },
  },
  {
    key: '_offtake.pilotage',
    group: 'port',
    label: 'Pilot boarding',
    tier: 'headline',
    showWhen: 'always',
    mode: 'hydrogen',
    compareOnly: true,
    better: 'lower',
    formatter: (p) => {
      const pil = projectValue(p, '_offtake.nautical_access.pilotage');
      if (!pil?.nearest_boarding_name) return cell({ text: NOT_ASSESSED, cls: 'na' });
      const nmv = pil.boarding_distance_nm;
      return cell({ text: pil.nearest_boarding_name,
                    meta: nmv != null ? `${Number(nmv).toFixed(1)} nm straight line from the site` : null,
                    num: nmv != null ? Number(nmv) : null });
    },
  },
  {
    key: 'nearest_port_depth_m',
    group: 'port',
    label: 'Port depth (register)',
    tier: 'headline',
    showWhen: 'always',
    mode: 'hydrogen',
    compareOnly: true,
    better: 'higher',
    warn: (p) => { const d = firstValue(p, ['nearest_port_depth_m', 'port_depth_m']); return d != null && d < 9; },
    formatter: (p) => {
      const d = firstValue(p, ['nearest_port_depth_m', 'port_depth_m']);
      if (d == null) return cell({ text: NOT_ASSESSED, cls: 'na' });
      const est = projectValue(p, 'nearest_port_depth_estimated');
      return cell({ text: `${d} m`, meta: est ? 'estimated' : null, num: Number(d) });
    },
  },
];

// ─── Public helpers ─────────────────────────────────────────────────────────

// The single point where a field's cls is determined. Both CompareTable and
// SelectedSiteCard call this — never .formatter directly. 'na' takes
// precedence (the field couldn't be evaluated); otherwise block > warn > ''.
export function groupIdFor(field, mode) {
  return (field.groupFor && field.groupFor[mode]) || field.group;
}

export function renderCell(field, project) {
  const base = field.formatter(project);
  if (base.cls === 'na') return base;
  if (field.block && field.block(project)) return { ...base, cls: 'block' };
  if (field.warn  && field.warn(project))  return { ...base, cls: 'warn'  };
  return { ...base, cls: '' };
}

// Fields for a group, filtered by mode. A `showWhen: flagged` field only
// appears in the card when its warn or block fires for THIS project.
// The `mode` argument is required — no default; the shell passes the
// project's use_case ('datacenter' | 'hydrogen').
export function headlineFieldsFor(groupId, project, mode) {
  return FIELDS
    .filter(f => groupIdFor(f, mode) === groupId)
    .filter(f => !f.compareOnly)
    .filter(f => modeMatches(f, mode))
    .filter(f => {
      // showWhen: function → invoked with the project; drop when it
      // returns falsy. Used e.g. by fi_owner_class to hide on NO/IT.
      if (typeof f.showWhen === 'function') return !!f.showWhen(project);
      if (f.showWhen !== 'flagged') return true;
      const w = f.warn  ? f.warn(project)  : false;
      const b = f.block ? f.block(project) : false;
      return w || b;
    });
}

// The one site that does best on a row, or -1: compares cell.num (display
// precision) in the field's `better` direction, and names no winner when
// fewer than two sites have a number or the best value is shared. Used by
// the compare table's green dot and the exports (utils/export).
export function bestIndex(field, cells) {
  if (!field.better) return -1;
  const nums = cells
    .map((c, i) => ({ i, v: c.num }))
    .filter(x => x.v != null && !Number.isNaN(x.v));
  if (nums.length < 2) return -1;
  nums.sort((a, b) => (field.better === 'higher' ? b.v - a.v : a.v - b.v));
  const winners = nums.filter(n => n.v === nums[0].v);
  return winners.length > 1 ? -1 : winners[0].i;
}

// All headline fields for the compare table. A `showWhen: flagged` row
// appears if ANY compared site trips its warn or block. A callable
// showWhen must return true for AT LEAST ONE compared site.
export function compareFieldsFor(projects, mode) {
  return FIELDS
    .filter(f => modeMatches(f, mode))
    .filter(f => {
      if (typeof f.showWhen === 'function') {
        return projects.some(p => !!f.showWhen(p));
      }
      if (f.showWhen !== 'flagged') return true;
      return projects.some(p => {
        const w = f.warn  ? f.warn(p)  : false;
        const b = f.block ? f.block(p) : false;
        return w || b;
      });
    });
}

// Derived: "N of 9 themes assessed". Denominator = THEME_KEYS.length.
// A theme counts as assessed iff it's present AND defaulted === false.
// A missing theme counts as NOT assessed.
export function confidence(project) {
  const themes = projectValue(project, 'readiness_themes') || {};
  const keys = themeKeysFor(project);
  const assessed = keys.filter(k => themes[k] && themes[k].defaulted === false).length;
  const total = keys.length;
  return { assessed, total, text: `${assessed} of ${total} themes assessed` };
}

// Themes ranked by score (defaulted ones INCLUDED here — filter downstream).
const THEME_LABELS = {
  power: 'Power', planning: 'Planning', water: 'Water',
  site_formation: 'Site formation', access: 'Access', fiber: 'Connectivity',
  environment: 'Environment', permitting: 'Permitting', expansion: 'Expansion',
  // ammonia_v1
  grid: 'Grid', port: 'Port', power_price: 'Power price', land: 'Land',
  setback: 'Setback',
};
export function themeDriver(themeKey, project) {
  switch (themeKey) {
    case 'power':
    case 'grid':           return formatDistanceKm(projectValue(project, 'nearest_grid_distance_km'));
    case 'port': {
      const hb = projectValue(project, 'harbour_screen.harbour');
      if (hb && hb.distance_km != null) return `${(HARBOUR_KIND[hb.kind] || 'harbour')} ${formatDistanceKm(hb.distance_km)}`;
      const km = firstValue(project, ['nearest_port_distance_km', 'port_dist_km']);
      return km != null ? `port ${formatDistanceKm(km)}` : null;
    }
    case 'power_price': {
      const p = projectValue(project, 'spot_30d_avg_nok_mwh');
      return p != null ? `${Math.round(p)} NOK/MWh` : null;
    }
    case 'land': {
      // The land theme scores the flat contiguous area when the slope grid
      // ran (ammonia_readiness._land), so name that figure.
      const flat = projectValue(project, 'buildable_contiguous_ha');
      if (flat != null) return `${formatAreaHa(flat)} flat`;
      const ha = parcelAreaHa(project);
      return ha != null ? formatAreaHa(ha) : null;
    }
    case 'setback': {
      const km = projectValue(project, 'nearest_protected_distance_km');
      return km != null ? `protected ${formatDistanceKm(km)}` : null;
    }
    case 'water':          return formatDistanceKm(projectValue(project, 'water_distance_km'));
    case 'site_formation': {
      const s = projectValue(project, 'slope_pct');
      return s != null ? `slope ${formatPercent(s, { fractionDigits: 1 })}` : null;
    }
    case 'access': {
      const km = projectValue(project, 'nearest_road_distance_km');
      return km != null ? `road ${formatDistanceKm(km)}` : null;
    }
    case 'fiber': {
      const km = projectValue(project, 'nearest_ixp_distance_km');
      return km != null ? `IXP ${formatDistanceKm(km)}` : null;
    }
    case 'environment': {
      const km = projectValue(project, 'nearest_protected_distance_km');
      return km != null ? `protected ${formatDistanceKm(km)}` : null;
    }
    default: return null;
  }
}
function themesRanked(project) {
  const t = projectValue(project, 'readiness_themes') || {};
  return Object.entries(t)
    .map(([k, v]) => ({ key: k, score: v.score_0_1, defaulted: v.defaulted }))
    .filter(x => x.score != null)
    .sort((a, b) => b.score - a.score);
}

// Best-of-two themes by score, non-defaulted only, with the driver value.
export function bestFor(project) {
  return themesRanked(project)
    .filter(t => t.defaulted === false)
    .slice(0, 2)
    .map(t => {
      const d = themeDriver(t.key, project);
      return `${THEME_LABELS[t.key] || t.key}${d ? ` (${d})` : ''}`;
    });
}
// Worst-two themes below 0.5, non-defaulted (defaulted themes belong to the
// data-gaps line, not to watchOut).
export function watchOut(project) {
  return themesRanked(project)
    .filter(t => t.defaulted === false && t.score < 0.5)
    .slice(-2)
    .reverse()
    .map(t => {
      const d = themeDriver(t.key, project);
      return `${THEME_LABELS[t.key] || t.key}${d ? ` (${d})` : ''}`;
    });
}

// Theme status for the group card's "Good / Fair / Poor" pill.
// Returns null when the group has no theme mapping (neighbours, economics).
export function groupStatus(group, project) {
  if (!group.themes.length) return null;
  const themes = projectValue(project, 'readiness_themes') || {};
  const scores = group.themes.map(k => themes[k]?.score_0_1).filter(v => v != null);
  if (!scores.length) return { cls: 'na', label: 'Not scored', estimated: false };
  const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
  const anyDefaulted = group.themes.some(k => themes[k]?.defaulted === true);
  let cls, label;
  if (avg >= 0.7) { cls = 'good'; label = 'Good'; }
  else if (avg >= 0.4) { cls = 'fair'; label = 'Fair'; }
  else { cls = 'poor'; label = 'Poor'; }
  if (anyDefaulted) label += ' (est.)';
  return { cls, label, estimated: anyDefaulted };
}
